import React from 'react';
import SEOHead from '@/components/SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Shield, Eye, Cookie, Database, Mail, AlertTriangle } from 'lucide-react';

export default function Privacy() {
  return (
    <>
      <SEOHead
        title="Politique de confidentialité | Impelatek"
        description="Politique de confidentialité et protection des données personnelles d'Impelatek. Informations sur l'utilisation des cookies et vos droits GDPR."
        canonical="https://impelatek.fr/privacy"
        ogType="website"
      />
      
      <div className="min-h-screen bg-gradient-to-br from-background to-muted/20">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <Shield className="h-8 w-8 text-primary mr-3" />
              <h1 className="text-3xl font-bold">Politique de confidentialité</h1>
            </div>
            <p className="text-muted-foreground">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          </div>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5" />
                  1. Collecte des données
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Chez Impelatek, nous respectons votre vie privée et nous nous engageons à protéger vos données personnelles.
                </p>
                <div className="space-y-3">
                  <div>
                    <h4 className="font-medium mb-2">Données collectées automatiquement :</h4>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                      <li>• Adresse IP</li>
                      <li>• Type et version du navigateur</li>
                      <li>• Pages visitées et temps passé</li>
                      <li>• Référent (site d'origine)</li>
                      <li>• Résolution d'écran et système d'exploitation</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Données collectées volontairement :</h4>
                    <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                      <li>• Formulaires de contact (nom, email, message)</li>
                      <li>• Préférences utilisateur (thème, langue)</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cookie className="h-5 w-5" />
                  2. Utilisation des cookies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Nous utilisons des cookies pour améliorer votre expérience et analyser l'utilisation du site.
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Badge variant="default" className="w-fit">
                      Cookies nécessaires
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      Essentiels au fonctionnement du site. Ne peuvent pas être désactivés.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Badge variant="secondary" className="w-fit">
                      Cookies d'analyse
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      Google Analytics pour comprendre l'utilisation du site.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Badge variant="outline" className="w-fit">
                      Cookies marketing
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      Publicités ciblées et mesure de leur efficacité.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Badge variant="destructive" className="w-fit">
                      Cookies de préférences
                    </Badge>
                    <p className="text-xs text-muted-foreground">
                      Mémorisation de vos préférences (thème, langue).
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-5 w-5" />
                  3. Utilisation des données
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Vos données sont utilisées pour :
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 ml-4">
                    <li>• Améliorer le contenu et la navigation du site</li>
                    <li>• Répondre à vos demandes de contact</li>
                    <li>• Analyser le trafic et l'utilisation du site</li>
                    <li>• Personnaliser votre expérience utilisateur</li>
                    <li>• Respecter nos obligations légales</li>
                  </ul>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  4. Vos droits GDPR
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Conformément au RGPD, vous disposez des droits suivants :
                  </p>
                  <div className="grid md:grid-cols-2 gap-3">
                    <div className="p-3 border rounded-lg">
                      <h4 className="font-medium text-sm">Droit d'accès</h4>
                      <p className="text-xs text-muted-foreground">
                        Connaître les données que nous détenons sur vous
                      </p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <h4 className="font-medium text-sm">Droit de rectification</h4>
                      <p className="text-xs text-muted-foreground">
                        Corriger les données inexactes
                      </p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <h4 className="font-medium text-sm">Droit à l'effacement</h4>
                      <p className="text-xs text-muted-foreground">
                        Demander la suppression de vos données
                      </p>
                    </div>
                    <div className="p-3 border rounded-lg">
                      <h4 className="font-medium text-sm">Droit à la portabilité</h4>
                      <p className="text-xs text-muted-foreground">
                        Récupérer vos données dans un format lisible
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  5. Contact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">
                    Pour toute question concernant cette politique de confidentialité ou pour exercer vos droits :
                  </p>
                  <div className="p-4 bg-muted/50 rounded-lg">
                    <p className="font-medium">Impelatek</p>
                    <p className="text-sm text-muted-foreground">Email : impelatek@gmail.com</p>
                    <p className="text-sm text-muted-foreground">
                      Délai de réponse : 30 jours maximum
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-orange-200 bg-orange-50/50 dark:border-orange-800 dark:bg-orange-950/20">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-orange-700 dark:text-orange-300">
                  <AlertTriangle className="h-5 w-5" />
                  6. Modifications
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-orange-600 dark:text-orange-400">
                  Cette politique de confidentialité peut être modifiée. 
                  Les changements significatifs vous seront notifiés par banner sur le site 
                  ou par email si nous avons votre adresse.
                </p>
              </CardContent>
            </Card>
          </div>

          <div className="mt-8 text-center">
            <p className="text-xs text-muted-foreground">
              Cette politique de confidentialité est conforme au Règlement Général sur la Protection des Données (RGPD) 
              et à la loi française Informatique et Libertés.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
